import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyPhone,
  usePropertyAddressUrl,
  useCollectionBinding,
  usePrimaryColor,
  useSecondaryColor,
  usePage,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, EditableCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import repliBlackLogoUrl from '../assets/repli_black.svg';
import { getContrastTextColor, TEXT_ON_PRIMARY_LIGHT } from '../utils/luminance';
// Parent pages list, shallow-merged with this theme's `src/pages.json`
// overrides (Future Residents Club, Contact position, off-by-default
// Deals / Dallas Mavericks Games / Qualifying Criteria). The runtime doesn't
// expose page metadata (position / parent / template) through a hook, and
// parent-src imports are lint errors. The parent's package.json doesn't list
// `./pages.json` in its exports map, but the platform's Vite alias resolves
// the bare specifier at build time.
import basePagesData from '@vibesites/theme-theme-homestead/pages.json';
import localPagesData from '../pages.json';

// ============================================================================
// Dynamic Navigation from pages.json
// ----------------------------------------------------------------------------
// Nav-page filter matches the Weidner Belmont / Aster header groupings so this
// company theme surfaces the same pages and dropdown structure. Desktop shows
// a utility bar (phone, address, Apply Now, Resident Access) above a
// single-row centered nav with Schedule a Tour as the far-right CTA; mobile
// keeps the Homestead hamburger + off-canvas drawer.
//
//   • `home` is an explicit nav link.
//   • `contact` is the last nav link.
//   • `apply-now` and `resident-access` are excluded from the row — they
//     surface in the desktop utility bar and the mobile drawer.
//   • Schedule a Tour remains in the Gallery & Tours group and also
//     surfaces as the far-right nav CTA (mirrors Aster / other REPLI themes).
//   • Three synthetic groups related pages under a single label:
//       - Find Your Home  → Floor Plans + Interactive Map
//       - Features        → Amenities + Pet Policy + Future Residents Club
//       - Gallery & Tours → Gallery + Virtual Tour + Schedule a Tour
//     Groups collapse to their single surviving child when the property
//     doesn't have the others (e.g. no interactive map). A multi-child
//     group still hyperlinks to its first surviving child so the parent
//     label is a real destination (Features → Amenities, Gallery & Tours
//     → Gallery, Find Your Home → Floor Plans).
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (indented row inside the slide-in drawer). */
  parent?: string;
}

export interface NavLink {
  alias?: string;
  label: string;
  href?: string;
  /** Sub-nav items rendered as indented rows inside the drawer. Populated
      when other pages declare `parent: <this alias>`, or when a synthetic
      group defines child aliases explicitly. */
  children?: NavLink[];
}

interface NavGroup {
  label: string;
  aliases: string[];
}

const NAV_EXCLUDED_ALIASES = new Set<string>([
  'privacy-policy',
  'sitemap',
  'accessibility',
  // Apply Now + Resident Access live in the desktop utility bar / drawer.
  'apply-now',
  'thank-you',
  'resident-access',
  // `schedule-a-tour` participates in the "Gallery & Tours" group and also
  // surfaces as the far-right nav CTA via `header.schedule_tour_link`.
  'hyperlocal',
  'reviews',
]);

const SYNTHETIC_NAV_GROUPS: NavGroup[] = [
  { label: 'Find Your Home', aliases: ['floor-plans', 'interactive-map'] },
  { label: 'Features', aliases: ['amenities', 'pet-policy', 'future-residents-club'] },
  { label: 'Gallery & Tours', aliases: ['gallery', 'virtual-tour', 'schedule-a-tour'] },
];

const SYNTHETIC_GROUP_ALIASES = new Set(SYNTHETIC_NAV_GROUPS.flatMap((group) => group.aliases));

/** Shallow-merge parent pages with this theme's pages.json overrides so nav
    sees local additions (Future Residents Club, off-by-default Deals /
    Dallas Mavericks Games / Qualifying Criteria) and field tweaks (Contact
    position) the same way the platform page manifest does. */
function mergePagesData(
  base: Record<string, PageEntry>,
  local: Record<string, Partial<PageEntry>>,
): Record<string, PageEntry> {
  const merged: Record<string, PageEntry> = { ...base };
  for (const [alias, override] of Object.entries(local)) {
    merged[alias] = { ...(merged[alias] ?? {}), ...override } as PageEntry;
  }
  return merged;
}

const pagesData = mergePagesData(
  basePagesData as Record<string, PageEntry>,
  localPagesData as Record<string, Partial<PageEntry>>,
);

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 *
 * Synthetic groups are ANCHORED to the FIRST alias in `aliases` — that
 * alias's page position places the group in the nav row.
 *
 * `hasInteractiveLeasing` gates the Interactive Map page — properties that
 * don't have the ApartmentSync (or override) config don't get the map page
 * in the nav, and the synthetic Find Your Home group collapses to plain
 * Floor Plans.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];
  const consumedAliases = new Set<string>();

  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { alias, label: page.name, href: page.path });
  }

  for (const [alias, page] of eligible) {
    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;
    if (page.parent && SYNTHETIC_GROUP_ALIASES.has(page.parent)) continue;
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    }
  }

  const emittedGroups = new Set<NavGroup>();
  const emitSyntheticGroup = (group: NavGroup) => {
    if (emittedGroups.has(group)) return;
    emittedGroups.add(group);
    const children = group.aliases
      .map((childAlias) => linksByAlias.get(childAlias))
      .filter((link): link is NavLink => !!link);
    for (const child of children) {
      if (child.alias) consumedAliases.add(child.alias);
    }
    if (children.length === 1) {
      topLevel.push(children[0]);
    } else if (children.length > 1) {
      // Parent label links to the first surviving child (the group's
      // anchor alias) so Features / Gallery & Tours / Find Your Home
      // are clickable, not just headings above the indented children.
      topLevel.push({
        label: group.label,
        href: children[0].href,
        children,
      });
    }
  };

  for (const [alias] of eligible) {
    if (consumedAliases.has(alias)) continue;

    const anchoredGroup = SYNTHETIC_NAV_GROUPS.find((item) => item.aliases[0] === alias);
    if (anchoredGroup) {
      emitSyntheticGroup(anchoredGroup);
      continue;
    }

    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;

    const page = linksByAlias.get(alias);
    const parent = eligible.find(([a]) => a === alias)?.[1].parent;
    if (parent && eligibleAliases.has(parent) && parent !== alias) continue;

    if (page) topLevel.push(page);
  }

  for (const group of SYNTHETIC_NAV_GROUPS) {
    emitSyntheticGroup(group);
  }

  return topLevel.filter((link) => !link.children || link.children.length > 0);
}

/** A nav link is "active" when its href matches the current page — or, for a
    parent / synthetic group with children, when any of its children match. */
function isNavLinkActive(link: NavLink, currentPath: string): boolean {
  const current = normalizePath(currentPath);
  if (link.href && normalizePath(link.href) === current) return true;
  return !!link.children?.some((c) => !!c.href && normalizePath(c.href) === current);
}

/** Add a leading "/" + normalize a trailing slash so two paths from different
    sources (pages.json `path`, runtime `page.path`, window.location.pathname)
    compare equal regardless of trailing-slash convention. */
function normalizePath(p: string | undefined | null): string {
  if (!p) return '';
  let s = p.trim();
  if (!s.startsWith('/')) s = `/${s}`;
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s;
}

// ============================================================================
// Office Hours formatting (mirrors Footer.tsx)
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by their canonical day key so we can look each
  // day up when walking the full week below.
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const groups: GroupedHours[] = [];

  // Walk every day of the week. Days without provided hours (e.g. Sunday)
  // render as "Closed" rather than being omitted.
  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const start = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${start} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Nav Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// Logo shown in the mobile/off-canvas menu. Separate from the nav-bar mark so
// editors can use a secondary or mono asset on the primary-colored drawer.
// Defaults to the secondary logo; render falls back to the nav logo, then a
// contrast mono, when the secondary is empty.
const drawerLogoSlotDef: SlotGroupDefinition = {
  label: 'Menu Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_secondary_logo',
      suggestedFields: [
        'property_secondary_logo',
        'property_primary_logo',
        'property_mono_white_logo',
        'property_mono_black_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// Company / management logo in the drawer credit row ("Website Managed By").
// Image defaults to the contrasting company mono at render time; the schema
// default is the dark mono so the editor picker has a sensible starting field.
const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_company_mono_black_logo',
      suggestedFields: [
        'property_company_mono_black_logo',
        'property_company_logo',
        'property_company_mono_white_logo',
        'property_secondary_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Schedule a Tour CTA — far-right button on the main nav row (sm+). Mirrors
// the Aster / other REPLI Weidner themes: Apply Now + Resident Access move
// into the utility bar, and this CTA occupies the nav's right edge. Defaults
// to the `/schedule-a-tour` page so editors can override destination, label,
// and target independently.
const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address — desktop utility bar + slide-in drawer ("Visit Us"). The URL has
// no defaultField — it defaults to usePropertyAddressUrl() at render time
// (mobile-safe Google Maps URL built from place / business / full address /
// lat-lon fallbacks). An explicit editor override wins.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icons
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Main Header Component
// ----------------------------------------------------------------------------
// STRUCTURE: Pinned (fixed) header with a desktop utility bar + white nav row
// (mirrors Aster / other REPLI Weidner themes).
//
// Desktop (lg+):
//   Utility bar (secondary brand):
//     [address] [phone] .................... [Apply Now] [Resident Access]
//   Main nav row (white):
//     [logo] .... [centered nav — slight left nudge] .... [Schedule a Tour]
//
// Tablet (sm → lg):
//   [hamburger]  [logo] ........... [Schedule a Tour]
//
// Mobile (< sm):
//   [hamburger]  [logo]
//   Page navigation lives in an off-canvas drawer that slides in from the
//   LEFT. The drawer is painted in the property's primary color and hosts
//   the nav links, office hours, Apply Now + Resident Access, the address
//   ("Visit Us"), the phone ("Let's Talk"), and the Multihub / Repli credits.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const desktopNavRef = useRef<HTMLElement>(null);
  // Grace period before a desktop dropdown closes. Moving from the label
  // into the menu crosses a small gap; closing immediately makes the box
  // vanish under the cursor. The timer is cancelled if the pointer re-enters.
  const dropdownCloseTimer = useRef<number | null>(null);

  // Subtle shadow once the page scrolls a little past the top.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Current page path — drives the active-link underline in the drawer.
  const pageCtx = usePage();
  const currentPath = useMemo(
    () =>
      normalizePath(
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.path],
  );

  // Credit-logo variant (white vs black) follows the same contrast logic the
  // Footer uses, so the Multihub / Repli marks stay legible on the drawer's
  // primary-color background regardless of how light/dark the brand color is.
  const primaryColor = usePrimaryColor();
  const useWhiteOnPrimary = getContrastTextColor(primaryColor) === TEXT_ON_PRIMARY_LIGHT;

  // Contrast-picked text for the secondary-colored utility bar (and matching
  // Schedule a Tour CTA). Computed from the resolved secondary rather than
  // trusting `--vibe-color-text-on-secondary` alone (mirrors Aster).
  const secondaryColor = useSecondaryColor();
  const utilOnSecondaryColor = getContrastTextColor(secondaryColor);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive leasing
  // view" widget. We require BOTH the JavaScript ID and the Config ID — a
  // property with only one populated is a half-configured state. Mirrors the
  // SSG-side hide + noindex logic in ssg-entry.ts.
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as string | null | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as string | null | undefined;

  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim());

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Content tokens — prefer the editable token (defaults to call tracking),
  // then usePropertyPhone() which falls back to property_phone.
  const phoneToken = useContentToken('header.phone');
  const trackedPhone = usePropertyPhone();
  const phone = phoneToken || trackedPhone || '(555) 555-5555';

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const drawerLogoRaw = useSlotGroup('header.drawer_logo', drawerLogoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const managedByLogo = useSlotGroup('header.managed_by_logo', managedByLogoDef);
  const companyMonoWhiteLogo = usePropertyField('property_company_mono_white_logo');
  const companyMonoBlackLogo = usePropertyField('property_company_mono_black_logo');
  const secondaryLogo = usePropertyField('property_secondary_logo');

  // Nav bar uses the nav logo slot (primary by default). The open menu uses its
  // own slot (secondary by default) so the two marks can differ. Mono is only
  // a fallback when both slot images are empty, picking the contrasting mono
  // so a missing color logo still reads on the primary fill.
  const barLogoImage = logoRaw.image || monoBlackLogo;
  const drawerLogoImage =
    drawerLogoRaw.image ||
    logoRaw.image ||
    (useWhiteOnPrimary ? monoWhiteLogo || monoBlackLogo : monoBlackLogo);

  // Managed-by credit: same contrast preference as the drawer property logo,
  // then the slot image, then secondary/company fallback (mirrors Footer).
  const managedByImage =
    (useWhiteOnPrimary ? companyMonoWhiteLogo : companyMonoBlackLogo) ||
    managedByLogo.image ||
    secondaryLogo;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();

  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', { collection: 'office_hours' });
  const groupedHours = officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  // Google Fonts — heading + body (Josefin Sans), accent (Dancing Script), and
  // Josefin Slab (the slab/western display face used by content section titles
  // like PropertyOverview). Loaded globally from the Header so every page
  // renders the theme's typography. (The hero titles stay on Josefin Sans.)
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="homestead-fonts"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@300;400;500;600;700&family=Josefin+Slab:wght@300;400;500;600;700&family=Dancing+Script:wght@500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'homestead-fonts');
    document.head.appendChild(link);
  }, []);

  // Measure header height for the layout spacer (the header is fixed, so the
  // page needs a placeholder of equal height to sit beneath it).
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  // Lock body scroll while the drawer is open + close on Escape.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Close the mobile drawer if the viewport crosses into desktop nav territory.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (mq.matches) setMenuOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const clearDropdownCloseTimer = useCallback(() => {
    if (dropdownCloseTimer.current != null) {
      window.clearTimeout(dropdownCloseTimer.current);
      dropdownCloseTimer.current = null;
    }
  }, []);

  const openDesktopDropdown = useCallback((id: string) => {
    clearDropdownCloseTimer();
    setOpenDropdown(id);
  }, [clearDropdownCloseTimer]);

  const closeDesktopDropdown = useCallback(() => {
    clearDropdownCloseTimer();
    setOpenDropdown(null);
  }, [clearDropdownCloseTimer]);

  const scheduleCloseDesktopDropdown = useCallback(() => {
    clearDropdownCloseTimer();
    dropdownCloseTimer.current = window.setTimeout(() => {
      dropdownCloseTimer.current = null;
      setOpenDropdown(null);
    }, 220);
  }, [clearDropdownCloseTimer]);

  useEffect(() => () => clearDropdownCloseTimer(), [clearDropdownCloseTimer]);

  // Desktop dropdown: close on outside click / Escape.
  useEffect(() => {
    if (!openDropdown) return;
    const onPointer = (e: MouseEvent) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(e.target as Node)) {
        closeDesktopDropdown();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDesktopDropdown();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openDropdown, closeDesktopDropdown]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      {/* ============================================================== */}
      {/* Pinned white header bar                                        */}
      {/* ============================================================== */}
      <header
        ref={headerRef}
        className={`fixed left-0 right-0 top-0 z-50 w-full bg-[var(--vibe-color-background)] transition-shadow duration-300${scrolled ? ' header-scrolled' : ''}`}
        style={{
          paddingTop: 'var(--offer-banner-height, 0px)',
          // Contrast-picked text/icon color for the secondary utility bar +
          // Schedule a Tour CTA. Falls back to text-on-secondary before tokens
          // hydrate.
          ...(utilOnSecondaryColor
            ? { ['--homestead-util-on-secondary' as string]: utilOnSecondaryColor }
            : {}),
        }}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav. Rendered
            visually hidden because it's configuration, not display copy. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>
        <EditableCollectionBinding
          bindingPath="header.virtual_tours"
          label="Virtual Tours"
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableCollectionBinding>

        {/* ============================================================ */}
        {/* Utility row — desktop only. Left: address + phone. Right:     */}
        {/* Apply Now + Resident Access. Mobile keeps these in the drawer. */}
        {/* ============================================================ */}
        <div className="header-util-row hidden lg:block">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-6 h-9">
              <div className="flex items-center gap-6 min-w-0">
                <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                  <a
                    href={addressLink.url || propertyAddressUrl || '#'}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="header-util-link header-util-address"
                    aria-label="View property on Google Maps"
                  >
                    {addressLink.address || '123 Main Street, Atlanta, GA 30313'}
                  </a>
                </EditableSlotGroup>

                <a
                  href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                  className="dniphone dniphonehref header-util-link"
                  aria-label="Call us"
                >
                  <EditableToken
                    path="header.phone"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>
              </div>

              <div className="flex items-center gap-6 flex-shrink-0">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="header-util-link"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="header-util-link"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        <div className="header-bar px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center h-20 lg:h-24 gap-4">
            {/* Hamburger — LEFT (mobile / tablet only) */}
            <button
              onClick={() => setMenuOpen(true)}
              className="relative w-10 h-10 flex flex-col items-center justify-center gap-[5px] flex-shrink-0 -ml-2 lg:hidden"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              <span className="block w-6 h-0.5 bg-[var(--vibe-color-text)]" />
              <span className="block w-6 h-0.5 bg-[var(--vibe-color-text)]" />
              <span className="block w-6 h-0.5 bg-[var(--vibe-color-text)]" />
            </button>

            {/* Logo — centered on mobile (CTA hidden there), left-aligned from sm up */}
            <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef} className="relative z-10 mx-auto sm:mx-0 flex-shrink-0">
              <a href="/" className="block">
                {barLogoImage ? (
                  <VibesitesImage
                    url={barLogoImage}
                    alt={logoRaw.alt || 'Community Logo'}
                    className="h-16 lg:h-20 w-auto object-contain"
                    sizes="220px"
                  />
                ) : (
                  <div className="font-heading text-2xl lg:text-3xl font-bold tracking-wide text-[var(--vibe-color-text)]">
                    The Rise
                  </div>
                )}
              </a>
            </EditableSlotGroup>

            {/* Desktop nav — true center, nudged slightly left off the CTA */}
            <nav
              ref={desktopNavRef}
              className="desktop-nav absolute left-1/2 top-1/2 z-[1] hidden lg:flex items-center gap-1 xl:gap-2"
              role="navigation"
              aria-label="Main navigation"
            >
              {navLinks.map((link) => {
                const active = isNavLinkActive(link, currentPath);
                const key = link.href || link.alias || link.label;
                const hasChildren = !!(link.children && link.children.length > 0);
                const dropdownId = key;
                const isOpen = openDropdown === dropdownId;

                if (!hasChildren) {
                  return link.href ? (
                    <a
                      key={key}
                      href={link.href}
                      aria-current={active ? 'page' : undefined}
                      className={`desktop-nav-link font-heading ${active ? 'is-active' : ''}`}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <span key={key} className={`desktop-nav-link font-heading ${active ? 'is-active' : ''}`}>
                      {link.label}
                    </span>
                  );
                }

                return (
                  <div
                    key={key}
                    className="desktop-nav-item relative"
                    onMouseEnter={() => openDesktopDropdown(dropdownId)}
                    onMouseLeave={scheduleCloseDesktopDropdown}
                  >
                    <div className="flex items-center">
                      {link.href ? (
                        <a
                          href={link.href}
                          aria-current={active ? 'page' : undefined}
                          className={`desktop-nav-link font-heading ${active || isOpen ? 'is-active' : ''}`}
                          aria-haspopup="true"
                          aria-expanded={isOpen}
                          onFocus={() => openDesktopDropdown(dropdownId)}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <button
                          type="button"
                          className={`desktop-nav-link font-heading ${active || isOpen ? 'is-active' : ''}`}
                          aria-haspopup="true"
                          aria-expanded={isOpen}
                          onClick={() => {
                            clearDropdownCloseTimer();
                            setOpenDropdown(isOpen ? null : dropdownId);
                          }}
                        >
                          {link.label}
                        </button>
                      )}
                      <button
                        type="button"
                        className="desktop-nav-chevron"
                        aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${link.label}`}
                        aria-expanded={isOpen}
                        onClick={() => {
                          clearDropdownCloseTimer();
                          setOpenDropdown(isOpen ? null : dropdownId);
                        }}
                      >
                        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 4.5L6 8l3.5-3.5" />
                        </svg>
                      </button>
                    </div>
                    <div
                      className={`desktop-nav-dropdown ${isOpen ? 'is-open' : ''}`}
                      role="menu"
                      onMouseEnter={() => openDesktopDropdown(dropdownId)}
                    >
                      {link.children!.map((child) => {
                        const childActive = !!child.href && normalizePath(child.href) === currentPath;
                        return (
                          <a
                            key={child.href || child.label}
                            href={child.href}
                            role="menuitem"
                            aria-current={childActive ? 'page' : undefined}
                            className={`desktop-nav-dropdown-link font-heading ${childActive ? 'is-active' : ''}`}
                            onClick={closeDesktopDropdown}
                          >
                            {child.label}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </nav>

            {/* Right group — Schedule a Tour (sm+; Apply Now / Resident Access
                live in the desktop utility bar and the mobile drawer) */}
            <div className="relative z-10 hidden sm:flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto">
              <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                <a
                  href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  className="header-cta header-cta--solid"
                >
                  {scheduleTourLink.label || 'Schedule a Tour'}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Mobile spacer mirroring the hamburger so the centered logo sits in the true center of the bar */}
            <div aria-hidden="true" className="w-10 -mr-2 flex-shrink-0 sm:hidden" />
          </div>
        </div>
      </header>

      {/* Layout spacer beneath the fixed bar. Falls back to the bar's fixed
          height classes before the measured height is available (SSG/first
          paint) so there's no content jump on hydration. */}
      <div
        aria-hidden="true"
        className={headerHeight ? '' : 'h-20 lg:h-24'}
        style={headerHeight ? { height: `${headerHeight}px` } : undefined}
      />

      {/* ============================================================== */}
      {/* Off-canvas drawer — slides in from the LEFT                    */}
      {/* ============================================================== */}
      <div
        className={`fixed inset-0 z-[60] transition-[visibility] duration-300 ${
          menuOpen ? 'visible' : 'invisible pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
      >
        {/* Backdrop */}
        <div
          onClick={closeMenu}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            menuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Panel — offset below the SpecialOfferBanner (which is fixed at
            top:0 with the same z-index) so its content, and the close button
            in particular, never tuck underneath the banner. Tracks the live
            `--offer-banner-height` the banner publishes, so it snaps back to
            the top when the banner is dismissed / absent. */}
        <aside
          className={`drawer-panel absolute left-0 w-full max-w-[1140px] overflow-y-auto shadow-2xl transition-transform duration-300 ease-out ${
            menuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          style={{
            top: 'var(--offer-banner-height, 0px)',
            height: 'calc(100% - var(--offer-banner-height, 0px))',
            backgroundColor: 'var(--vibe-color-primary)',
            color: 'var(--vibe-color-text-on-primary)',
          }}
        >
          <div className="relative flex min-h-full flex-col px-7 py-7 sm:px-10 sm:py-9 lg:px-14 lg:py-12">
            {/* Close */}
            <button
              onClick={closeMenu}
              className="drawer-close absolute top-6 left-6 sm:top-8 sm:left-9 lg:top-10 lg:left-12 w-10 h-10 flex items-center justify-center"
              aria-label="Close menu"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>

            {/* Main two-zone content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-10 pt-16 lg:pt-12">
              {/* ---- Left zone: logo, nav links, office hours ---- */}
              <div className="lg:pr-8">
                <EditableSlotGroup groupPath="header.drawer_logo" definition={drawerLogoSlotDef}>
                  <a href="/" onClick={closeMenu} className="inline-block">
                    {drawerLogoImage ? (
                      <VibesitesImage
                        url={drawerLogoImage}
                        alt={drawerLogoRaw.alt || logoRaw.alt || 'Community Logo'}
                        className="h-12 lg:h-14 w-auto object-contain"
                        sizes="160px"
                      />
                    ) : (
                      <div className="font-heading text-2xl lg:text-3xl font-bold tracking-wide">The Rise</div>
                    )}
                  </a>
                </EditableSlotGroup>

                <nav className="mt-8 lg:mt-10 flex flex-col" role="navigation" aria-label="Main navigation">
                  {navLinks.map((link) => {
                    const active = isNavLinkActive(link, currentPath);
                    const key = link.href || link.alias || link.label;
                    return (
                      <React.Fragment key={key}>
                        {link.href ? (
                          <a
                            href={link.href}
                            onClick={closeMenu}
                            aria-current={active ? 'page' : undefined}
                            className={`drawer-nav-link font-heading text-lg lg:text-xl py-2.5 ${active ? 'is-active' : ''}`}
                          >
                            {link.label}
                          </a>
                        ) : (
                          <div className={`drawer-nav-link font-heading text-lg lg:text-xl py-2.5 ${active ? 'is-active' : ''}`}>
                            {link.label}
                          </div>
                        )}
                        {link.children?.map((child) => {
                          const childActive = !!child.href && normalizePath(child.href) === currentPath;
                          return (
                            <a
                              key={child.href || child.label}
                              href={child.href}
                              onClick={closeMenu}
                              aria-current={childActive ? 'page' : undefined}
                              className={`drawer-nav-link font-heading text-base lg:text-lg py-2 pl-5 opacity-90 ${childActive ? 'is-active' : ''}`}
                            >
                              {child.label}
                            </a>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </nav>

                {groupedHours.length > 0 && (
                  <EditableCollectionBinding bindingPath="header.office_hours" label="Office Hours">
                    <div className="mt-10 lg:mt-12">
                      <h3 className="font-heading text-base lg:text-lg font-semibold mb-3">Office Hours</h3>
                      <dl className="space-y-1.5 max-w-[300px]">
                        {groupedHours.map((group, index) => (
                          <div key={index} className="flex justify-between gap-6 font-body text-sm opacity-85">
                            <dt>{group.label}</dt>
                            <dd className="text-right">{group.hours}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </EditableCollectionBinding>
                )}
              </div>

              {/* ---- Right zone: CTAs, address, phone ---- */}
              <div className="flex flex-col gap-8">
                {/* CTAs — always side-by-side, equal width */}
                <div className="flex flex-row gap-3">
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef} className="flex-1 min-w-0">
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      onClick={closeMenu}
                      className="drawer-cta"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef} className="flex-1 min-w-0">
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      onClick={closeMenu}
                      className="drawer-cta"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>
                </div>

                {/* Visit Us */}
                <div>
                  <h3 className="font-heading text-lg lg:text-xl font-semibold mb-2">Visit Us</h3>
                  <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                    <a
                      href={addressLink.url || propertyAddressUrl || '#'}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      onClick={closeMenu}
                      className="drawer-text-link inline-flex items-start gap-2 font-body text-sm lg:text-base opacity-90"
                    >
                      <MapPinIcon className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span className="whitespace-pre-line">{addressLink.address}</span>
                    </a>
                  </EditableSlotGroup>
                </div>

                {/* Let's Talk */}
                <div>
                  <h3 className="font-heading text-lg lg:text-xl font-semibold mb-2">Let&apos;s Talk</h3>
                  <a
                    href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                    className="dniphone dniphonehref drawer-text-link inline-flex items-center gap-2 font-body text-sm lg:text-base opacity-90"
                  >
                    <PhoneIcon className="w-4 h-4 flex-shrink-0" />
                    <EditableToken
                      path="header.phone"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>
              </div>
            </div>

            {/* ---- Credits: Managed By / Powered By / Designed By ---- */}
            <div className="mt-auto pt-10 lg:pt-14">
              <div className="flex flex-wrap items-end gap-x-16 gap-y-6">
                <EditableSlotGroup groupPath="header.managed_by_logo" definition={managedByLogoDef}>
                  {managedByImage ? (
                    <a
                      href={managedByLogo.url || '#'}
                      target={managedByLogo._target || '_blank'}
                      rel="noopener noreferrer"
                      className="block opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <span className="block font-body text-[10px] uppercase tracking-[0.15em] opacity-70 mb-2">
                        Website Managed By
                      </span>
                      <VibesitesImage
                        url={managedByImage}
                        alt={managedByLogo.alt || 'Property Management Company Logo'}
                        height={26}
                        className="h-[26px] w-auto max-w-[140px] object-contain"
                        sizes="140px"
                      />
                    </a>
                  ) : null}
                </EditableSlotGroup>
                <a
                  href="https://www.multihub.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block opacity-90 hover:opacity-100 transition-opacity"
                >
                  <span className="block font-body text-[10px] uppercase tracking-[0.15em] opacity-70 mb-2">Website Powered By</span>
                  {useWhiteOnPrimary ? (
                    <MultihubLogoMonoWhite height={26} alt="Multihub" />
                  ) : (
                    <MultihubLogoMonoBlack height={26} alt="Multihub" />
                  )}
                </a>
                <a
                  href="https://www.repli360.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block opacity-90 hover:opacity-100 transition-opacity"
                >
                  <span className="block font-body text-[10px] uppercase tracking-[0.15em] opacity-70 mb-2">Website Designed By</span>
                  <VibesitesImage
                    url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                    alt="Repli Logo"
                    height={26}
                    className="h-[26px] w-auto"
                    sizes="80px"
                  />
                </a>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        /* Pinned white header bar */
        .header-bar {
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .header-scrolled {
          box-shadow:
            0 1px 3px rgba(0,0,0,0.05),
            0 4px 14px rgba(0,0,0,0.04);
        }

        /* Utility row — solid secondary brand strip above the main nav
           (desktop only; mobile surfaces the same links in the drawer). */
        .header-util-row {
          background-color: var(--vibe-color-secondary);
        }
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: color-mix(
            in srgb,
            var(--homestead-util-on-secondary, var(--vibe-color-text-on-secondary)) 88%,
            transparent
          );
          white-space: nowrap;
          transition: color 0.2s ease;
        }
        .header-util-link:hover {
          color: var(--homestead-util-on-secondary, var(--vibe-color-text-on-secondary));
        }
        /* Address stays sentence case — uppercase + wide tracking makes a
           full street address hard to scan in the narrow util strip. */
        .header-util-link.header-util-address {
          text-transform: none;
          letter-spacing: 0.02em;
          font-weight: 500;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Header CTA buttons */
        .header-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem 0.7rem;
          font-family: var(--vibe-font-body);
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: none;
          border-radius: 2px;
          white-space: nowrap;
          transition: background-color 0.25s ease, color 0.25s ease, opacity 0.25s ease;
        }
        @media (min-width: 640px) {
          .header-cta { padding: 0.95rem 1.75rem; font-size: 0.875rem; }
        }
        .header-cta--solid {
          background: var(--vibe-color-secondary);
          color: var(--homestead-util-on-secondary, var(--vibe-color-text-on-secondary));
        }
        .header-cta--solid:hover {
          background: color-mix(in srgb, var(--vibe-color-secondary), black 12%);
        }

        /* ---- Desktop single-row centered nav ---- */
        /* True horizontal center, then a slight left nudge so the cluster
           clears the CTA button without looking off-center. */
        .desktop-nav {
          transform: translate(-50%, -50%) translateX(-0.75rem);
        }
        @media (min-width: 1280px) {
          .desktop-nav {
            transform: translate(-50%, -50%) translateX(-1rem);
          }
        }
        .desktop-nav-link {
          display: inline-flex;
          align-items: center;
          padding: 0.4rem 0.5rem;
          font-size: 0.8125rem;
          font-weight: 500;
          letter-spacing: 0.02em;
          color: var(--vibe-color-text);
          white-space: nowrap;
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          background: transparent;
          border: 0;
          cursor: pointer;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, opacity 0.2s ease;
        }
        @media (min-width: 1280px) {
          .desktop-nav-link { padding: 0.45rem 0.65rem; font-size: 0.875rem; }
        }
        .desktop-nav-link:hover,
        .desktop-nav-link.is-active {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        .desktop-nav-chevron {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-left: -0.15rem;
          margin-right: 0.15rem;
          padding: 0.35rem 0.2rem;
          color: var(--vibe-color-text);
          opacity: 0.7;
          background: transparent;
          border: 0;
          cursor: pointer;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .desktop-nav-item:hover .desktop-nav-chevron,
        .desktop-nav-chevron[aria-expanded="true"] {
          opacity: 1;
          transform: rotate(180deg);
        }
        .desktop-nav-dropdown {
          position: absolute;
          top: calc(100% + 0.35rem);
          left: 50%;
          min-width: 13.5rem;
          padding: 0.35rem 0;
          background: var(--vibe-color-background);
          border: 1px solid var(--vibe-color-border);
          box-shadow:
            0 4px 12px rgba(0,0,0,0.06),
            0 1px 3px rgba(0,0,0,0.04);
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transform: translateX(-50%) translateY(4px);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
          z-index: 20;
        }
        /* Invisible bridge across the gap between the label and the menu.
           Without it, the pointer leaves the nav item on the way down and
           the menu closes before the click lands. */
        .desktop-nav-dropdown::before {
          content: "";
          position: absolute;
          left: -0.75rem;
          right: -0.75rem;
          bottom: 100%;
          height: 0.6rem;
        }
        /* :hover keeps the menu open while the pointer is on the label,
           the gap bridge, or a sublink — no wait for React state. .is-open
           covers the click, keyboard, and the short close delay. */
        .desktop-nav-item:hover > .desktop-nav-dropdown,
        .desktop-nav-dropdown.is-open {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transform: translateX(-50%) translateY(0);
        }
        .desktop-nav-dropdown-link {
          display: block;
          padding: 0.7rem 1.15rem;
          font-size: 0.875rem;
          font-weight: 500;
          line-height: 1.3;
          color: var(--vibe-color-text);
          white-space: nowrap;
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 4px;
          transition: text-decoration-color 0.2s ease, background-color 0.2s ease;
        }
        .desktop-nav-dropdown-link:hover,
        .desktop-nav-dropdown-link.is-active {
          text-decoration-color: currentColor;
          background: color-mix(in srgb, var(--vibe-color-text) 4%, transparent);
        }

        /* ---- Drawer ---- */
        .drawer-close {
          color: var(--vibe-color-text-on-primary);
          opacity: 0.85;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .drawer-close:hover { opacity: 1; transform: rotate(90deg); }

        /* Drawer nav links: animated underline on hover, persistent underline
           when active (current page). */
        .drawer-nav-link {
          color: var(--vibe-color-text-on-primary);
          width: fit-content;
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, opacity 0.2s ease;
        }
        .drawer-nav-link:hover,
        .drawer-nav-link.is-active {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
          opacity: 1;
        }

        /* Drawer address / phone links */
        .drawer-text-link {
          color: var(--vibe-color-text-on-primary);
          transition: opacity 0.2s ease;
        }
        .drawer-text-link:hover { opacity: 1; }

        /* Drawer CTA buttons */
        .drawer-cta {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-width: 0;
          padding: 0.95rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: none;
          border-radius: 2px;
          text-align: center;
          white-space: normal;
          background: var(--vibe-color-secondary);
          color: var(--homestead-util-on-secondary, var(--vibe-color-text-on-secondary));
          transition: background-color 0.25s ease;
        }
        .drawer-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-secondary), black 12%);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Nav Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.drawer_logo': {
      label: 'Menu Logo',
      fields: {
        image: {
          label: 'Logo Image',
          type: 'image',
          defaultField: 'property_secondary_logo',
          suggestedFields: [
            'property_secondary_logo',
            'property_primary_logo',
            'property_mono_white_logo',
            'property_mono_black_logo',
          ],
        },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.managed_by_logo': {
      label: 'Managed By Logo',
      fields: {
        image: {
          label: 'Logo Image',
          type: 'image',
          defaultField: 'property_company_mono_black_logo',
          suggestedFields: [
            'property_company_mono_black_logo',
            'property_company_logo',
            'property_company_mono_white_logo',
            'property_secondary_logo',
          ],
        },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
        url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule a Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Header;
